import { api, type PrintProfileInfo } from '../sync/api';
import { thDateTime } from '../time';
import { $ } from './dom';
import { toast } from './toast';

/*
 * Admin → ตั้งค่า → "โปรไฟล์สีสำหรับ PDF โรงพิมพ์": pick the built-in GRACoL 2013 or a printer's own
 * CMYK .icc (uploaded here, checked by the server). Every refusal is shown with its reason.
 */

const errText = (e: unknown) => e instanceof Error ? e.message : String(e);
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};
const SAMPLES: Array<{ label: string; hex: string }> = [{ label: 'เขียว', hex: '#2F6B3A' }, { label: 'ทอง', hex: '#E0A526' }, { label: 'ม่วง', hex: '#6B3FD9' }];

export function initPrintProfileCard() {
  const listBox = $('adProfiles'), msg = $('adIccMsg'), saveBtn = $('adIccSave') as HTMLButtonElement;
  const file = $('adIccFile') as HTMLInputElement, pickBtn = $('adIccPick') as HTMLButtonElement;
  let profiles: PrintProfileInfo[] = [];
  let activeId = '', picked = '';
  let busy = false;
  let armedDelete = '';
  let sampleRun = 0;

  const say = (text: string, ok = false) => { msg.textContent = text; msg.hidden = !text; msg.classList.toggle('ok', ok); };

  function paint() {
    listBox.textContent = '';
    for (const p of profiles) {
      const row = el('label', 'profopt');
      const radio = el('input');
      radio.type = 'radio'; radio.name = 'adProfile'; radio.value = p.id; radio.checked = p.id === picked; radio.disabled = busy;
      radio.onchange = () => { picked = p.id; paint(); void sample(); };
      const text = el('span');
      text.append(el('b', '', `${p.name}${p.id === activeId ? ' · ใช้อยู่' : ''}`), el('small', '', p.note + (p.bytes ? ` · ${(p.bytes / 1024 / 1024).toFixed(1)} MB` : '')));
      row.append(radio, text);
      if (!p.builtIn && p.id !== activeId) {
        const del = el('button', 'btn danger', armedDelete === p.id ? 'แตะอีกครั้งเพื่อลบ' : 'ลบ');
        del.type = 'button'; del.disabled = busy;
        del.onclick = e => { e.preventDefault(); void remove(p); };
        row.append(del);
      }
      listBox.append(row);
    }
    saveBtn.disabled = busy || !picked || picked === activeId;
    pickBtn.disabled = busy;
  }

  async function load(keepMsg = false) {
    if (!keepMsg) say('');
    try {
      const r = await api.admin.printProfiles();
      profiles = r.profiles; activeId = r.activeId;
      if (!profiles.some(p => p.id === picked)) picked = activeId;
      $('adIccInfo').textContent = r.changedAt ? `เลือกล่าสุด ${thDateTime(new Date(r.changedAt))} โดย ${r.changedBy}` : 'ยังไม่เคยเลือก ใช้ค่าเริ่มต้น (GRACoL 2013)';
      if (r.missing) say('โปรไฟล์ที่เลือกไว้ถูกลบไปแล้ว ตอนนี้ทุกคนใช้ค่าเริ่มต้น (GRACoL 2013) แทน');
      paint(); void sample();
    } catch (e) { say('โหลดรายการโปรไฟล์สีไม่สำเร็จ: ' + errText(e)); }
  }

  /** a few of the group's colours in CMYK with the selected profile, so the admin sees what changes */
  async function sample() {
    const run = ++sampleRun;
    const box = $('adIccSample');
    const p = profiles.find(x => x.id === picked);
    if (!p) { box.textContent = ''; return; }
    box.textContent = 'กำลังคำนวณตัวอย่างสี…';
    try {
      const [{ createConverter }, wasm, res] = await Promise.all([
        import('../print/color'), import('lcms-wasm/dist/lcms.wasm?url'), fetch(p.url, { credentials: 'same-origin' })
      ]);
      if (!res.ok) throw new Error(`โหลดไฟล์โปรไฟล์ไม่ได้ (${res.status})`);
      const conv = await createConverter(new Uint8Array(await res.arrayBuffer()), wasm.default);
      const pc = (v: number) => Math.round(v * 100);
      let parts: string[];
      try {
        parts = SAMPLES.map(s => {
          const n = parseInt(s.hex.slice(1), 16);
          const [c, m, y, k] = conv.flat({ r: n >> 16, g: (n >> 8) & 255, b: n & 255, a: 1 });
          return `${s.label} ${s.hex} → C${pc(c)} M${pc(m)} Y${pc(y)} K${pc(k)}`;
        });
      } finally { conv.close(); }
      if (run === sampleRun) box.textContent = `ตัวอย่างสีกับโปรไฟล์นี้: ${parts.join(' · ')} · ดำล้วนของตัวหนังสือและเส้น → K100`;
    } catch (e) {
      if (run === sampleRun) box.textContent = 'คำนวณตัวอย่างสีไม่ได้: ' + errText(e);
    }
  }

  saveBtn.onclick = async () => {
    if (busy || !picked) return;
    busy = true; paint(); say('');
    try {
      await api.admin.useProfile(picked);
      const name = profiles.find(p => p.id === picked)?.name ?? '';
      await load(true);
      say(`ตั้งให้ทุกคนใช้ "${name}" แล้ว มีผลกับ PDF โรงพิมพ์ที่สร้างหลังจากนี้`, true);
    } catch (e) { say('บันทึกไม่สำเร็จ: ' + errText(e)); void load(true); }
    finally { busy = false; paint(); }
  };

  pickBtn.onclick = () => file.click();
  file.onchange = async () => {
    const f = file.files?.[0];
    file.value = '';
    if (!f || busy) return;
    if (!/\.(icc|icm)$/i.test(f.name)) { say(`ไฟล์ "${f.name}" ไม่ใช่ไฟล์โปรไฟล์สี (.icc หรือ .icm)`); return; }
    busy = true; paint(); say(`กำลังอัปโหลด "${f.name}"…`, true);
    try {
      const r = await api.admin.uploadProfile(f);
      picked = r.profile.id;
      await load(true);
      say(`อัปโหลด "${r.profile.name}" แล้ว ยังไม่ได้ใช้ กด "ใช้โปรไฟล์ที่เลือก" เพื่อให้ทุกคนใช้`, true);
      toast('อัปโหลดโปรไฟล์สีแล้ว');
    } catch (e) { say(`อัปโหลด "${f.name}" ไม่สำเร็จ: ${errText(e)}`); void load(true); }
    finally { busy = false; paint(); }
  };

  async function remove(p: PrintProfileInfo) {
    if (armedDelete !== p.id) {
      armedDelete = p.id; paint();
      setTimeout(() => { if (armedDelete === p.id) { armedDelete = ''; paint(); } }, 4000);
      return;
    }
    armedDelete = ''; busy = true; paint(); say('');
    try {
      await api.admin.deleteProfile(p.id);
      if (picked === p.id) picked = activeId;
      await load(true);
      say(`ลบ "${p.name}" แล้ว`, true);
    } catch (e) { say('ลบไม่สำเร็จ: ' + errText(e)); void load(true); }
    finally { busy = false; paint(); }
  }

  return { load };
}
